/**
 * Inline brand pill: subtle purple chip with the SideB "s." dot-logo + "sideb.io"
 *
 * Em-based sizing so the pill scales with the parent font size — looks right in
 * body copy, list items, H2/H3 headings, and small-print footnotes alike.
 *
 * Props:
 *   - variant: "light" (default) for white/light bgs, "dark" for navy hero/ROI surfaces.
 *   - asStatic: when true, renders as a styled <span> instead of an <a> — use when the
 *     pill is nested inside another clickable parent (e.g., a card Link wrapper) to
 *     avoid invalid <a>-in-<a> nesting.
 */
export function SidebPill({ variant = "light", asStatic = false, className = "", ...rest }) {
  const base =
    "inline-flex items-center gap-[0.3em] px-[0.4em] py-[0.15em] rounded-md font-medium text-[0.92em] leading-none align-middle whitespace-nowrap transition-colors";

  const palette =
    variant === "dark"
      ? "bg-white/10 hover:bg-white/[0.18] text-white"
      : "bg-[#ac62ea]/10 hover:bg-[#ac62ea]/20 text-[#7d3fbf] hover:text-[#5d2ba0]";

  const content = (
    <>
      <span
        className="inline-flex items-center justify-center rounded-[3px] bg-[#0b1e5a] text-white font-display font-bold tracking-tight leading-none"
        style={{ width: "1.2em", height: "1.2em", fontSize: "0.72em" }}
        aria-hidden
      >
        <span>s</span>
        <span style={{ color: "#ac62ea" }}>.</span>
      </span>
      <span className="leading-none">sideb.io</span>
    </>
  );

  if (asStatic) {
    return (
      <span
        data-testid="sideb-pill"
        className={`${base} ${palette} ${className}`}
        {...rest}
      >
        {content}
      </span>
    );
  }

  return (
    <a
      href="https://sideb.io"
      target="_blank"
      rel="noopener noreferrer"
      data-testid="sideb-pill"
      className={`${base} ${palette} ${className}`}
      {...rest}
    >
      {content}
    </a>
  );
}

export default SidebPill;
